/**
 * Domain normalization + validation — intended to become the ONE implementation.
 *
 * A "domain" reaches us as free text from several places (the agency signup
 * field, a brand URL, a competitor the user types, a cited source), and it is
 * used as a KEY in places where an exact match matters: the HubSpot Company
 * search, the cross-tenant DomainProfile cache, favicon lookups. A typo or a
 * pasted `https://www.x.com/page?utm=…` silently becomes a different key, so
 * normalizing before compare/store is load-bearing rather than cosmetic.
 *
 * MIGRATION STATUS — this is NOT yet the only copy (as of 2026-07-30). It was
 * written for the agency-domain gate + the hubspot-sync CRM key, and only the
 * Lambda's own private copy was actually deleted. Four pre-existing
 * implementations remain, untouched on purpose (they work, and rewriting the
 * competitor flows was out of scope for that change):
 *
 *   - `CompetitorsService.normalizeDomain` / `.looksLikeDomain` (api)
 *   - `isValidDomain` (CompetitorMatrixView.tsx)
 *   - `isValidCompetitorDomain` + a local normalizer (BrandOnboarding.tsx)
 *
 * How they relate today (checked, not assumed): the three validators are
 * **character-for-character the same regex** as `looksLikeDomain` below, so
 * validation genuinely agrees. The normalizers are **narrower** — they strip only
 * `https?://` and don't handle userinfo, a port, or another scheme:
 *
 *   input                    normalizeDomain    the legacy copies
 *   contact@agence.com   →   agence.com         contact@agence.com
 *   acme.com:8443        →   acme.com           acme.com:8443
 *   ftp://files.acme.com →   files.acme.com     ftp:            ← wrong, not merely unstripped
 *
 * The first two are caught downstream (the validator rejects them, so they fail
 * closed). The third silently yields a bogus host, which is the one real bug in
 * the set — reason enough to prefer this file at every new call site.
 *
 * Consequence to keep in mind: **editing this file does not change those four
 * call sites.** Migrating them is mechanical and worth doing next time any of
 * them is touched. Until then, "kept in shared so api/frontend/Lambdas agree"
 * is the goal, not yet a guarantee.
 */

/**
 * Strip everything that isn't the hostname and lowercase it: scheme, userinfo,
 * port, path, query, fragment, and a leading `www.`. Does NOT validate — pair
 * it with `looksLikeDomain` when the result is used as a key.
 */
export function normalizeDomain(input: string | undefined | null): string {
  let d = (input || '').trim().toLowerCase();
  if (!d) return '';
  d = d.replace(/^[a-z][a-z0-9+.-]*:\/\//, ''); // scheme
  d = d.split('/')[0].split('?')[0].split('#')[0]; // path / query / fragment
  // Strip userinfo, and salvage the host from a value that is actually an email
  // ("contact@agence.com" → "agence.com") — a plausible thing to paste into a
  // "your domain" field.
  d = d.split('@').pop() || d;
  d = d.split(':')[0]; // port
  return d.replace(/^www\./, '');
}

/**
 * Validate an ALREADY-normalized domain: at least one dot, an alphabetic TLD of
 * 2+ chars, only host-legal characters. Rejects a company name slugged into a
 * fake host ("acme corp", "acmecorp"), which is the failure mode that poisons
 * shared caches.
 */
export function looksLikeDomain(domain: string | undefined | null): boolean {
  const d = (domain || '').trim().toLowerCase();
  if (!d) return false;
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d) && /\.[a-z]{2,}$/.test(d);
}

/**
 * Two-label public suffixes, so `sodastream.co.uk` yields `sodastream`, not `co`.
 * Deliberately NOT the 9k-entry Public Suffix List — this exists for one string
 * compare; the list covers the suffixes real measured brands use.
 */
const TWO_LABEL_SUFFIXES = new Set([
  'co.uk', 'org.uk', 'ac.uk', 'gov.uk', 'co.jp', 'or.jp', 'ne.jp',
  'com.au', 'net.au', 'org.au', 'com.br', 'com.mx', 'com.ar', 'com.co',
  'co.nz', 'co.za', 'com.tr', 'com.cn', 'com.hk', 'com.sg', 'com.tw',
  'com.my', 'co.in', 'co.kr', 'co.il', 'com.pl', 'com.ua', 'com.ph',
]);

/**
 * The distinctive label of a host — `support-fr.sodastream.com` → `sodastream`.
 * `''` for anything that isn't a host (a free-text brand name has no dot).
 */
export function registrableLabel(host: string | undefined | null): string {
  const parts = (host || '').toLowerCase().replace(/^\.+|\.+$/g, '').split('.').filter(Boolean);
  if (parts.length < 2) return '';
  if (parts.length >= 3 && TWO_LABEL_SUFFIXES.has(parts.slice(-2).join('.'))) {
    return parts[parts.length - 3];
  }
  return parts[parts.length - 2];
}

/**
 * True iff `host` is the brand's OWN domain: the same host, a subdomain of it, or
 * the same registrable label under a different public suffix.
 *
 * Both arguments must already be bare hosts (`normalizeDomain` them first).
 *
 * The third rule is what makes `sodastream.com` and `support-fr.sodastream.com`
 * count for a brand stored as `https://sodastream.fr/` — added 2026-09-04 after a
 * real miss: a French brand whose global site is the `.com` had its own pages
 * scored as third-party (no `brandIsSource`, no green source chip, authority
 * 0/25 instead of 85/100) and its own support subdomain offered back as a
 * competitor to track. Tradeoff accepted: an unrelated company on another TLD
 * (`lemonade.fr` vs `lemonade.com`) now reads as the brand, so the label must be
 * ≥4 chars to keep short generic collisions (`bp`, `sfr`) out.
 *
 * The subdomain match requires the leading dot — plain `endsWith` would match the
 * evil-twin `fakevitalis-poitiers.fr` for a brand of `vitalis-poitiers.fr`.
 *
 * MUST stay in lock-step with `scoring.is_brand_host` in the Brand Perception
 * agent, which computes the persisted `brandIsSource` this UI renders.
 */
export function isBrandOwnedHost(
  host: string | undefined | null,
  brandHost: string | undefined | null,
): boolean {
  const h = (host || '').toLowerCase().trim();
  const b = (brandHost || '').toLowerCase().trim();
  if (!h || !b) return false;
  if (h === b || h.endsWith('.' + b)) return true;
  const label = registrableLabel(b);
  return label.length >= 4 && registrableLabel(h) === label;
}

/**
 * Normalize + validate in one step. Returns `''` when the input can't be a
 * hostname, so callers can treat falsy as "no usable domain" rather than
 * carrying a junk value forward. This is the function most callers want.
 */
export function toValidDomain(input: string | undefined | null): string {
  const d = normalizeDomain(input);
  return looksLikeDomain(d) ? d : '';
}
